// Связь с API пульта (задача D4): отвечает ли `ai-qual-api`. Отметки ставит клиент API: любой ответ сервиса —
// `markOk`, «Сервис Анны не отвечает» — `markFail`. Читают баннер «Нет связи — данные на …» (D5) и «Готовность»
// (D7): через 2 минуты без ответа — «● Сервис Анны не отвечает с … МСК» (Review Focus 2).
import { useSyncExternalStore } from 'react';

/** Через сколько без удачного ответа сервис считается лежащим: 2 минуты. */
export const SERVICE_DOWN_AFTER_MS = 120_000;

export interface ConnectionState {
  /** `offline` — последний запрос не дошёл до сервиса. */
  readonly state: 'online' | 'offline';
  /** Последний ответ сервиса, мс эпохи; `null` — ответов ещё не было. */
  readonly lastOkAt: number | null;
  /** С какого момента идёт сбой; `null` — сбоя нет. */
  readonly failingSince: number | null;
}

const INITIAL: ConnectionState = { state: 'online', lastOkAt: null, failingSince: null };

let current: ConnectionState = INITIAL;
const listeners = new Set<() => void>();

function set(next: ConnectionState): void {
  current = next;
  for (const listener of listeners) listener();
}

/** Сервис ответил в момент `t`. */
export function markOk(t: number): void {
  set({ state: 'online', lastOkAt: t, failingSince: null });
}

/** Сервис не ответил в момент `t`. Повторный сбой начало сбоя не сдвигает и экран не перерисовывает. */
export function markFail(t: number): void {
  if (current.failingSince !== null) return;
  set({ state: 'offline', lastOkAt: current.lastOkAt, failingSince: t });
}

export function getConnection(): ConnectionState {
  return current;
}

/**
 * Сервис лежит: идёт сбой и с последнего ответа прошло не меньше 2 минут. Ответов не было вовсе — отсчёт от начала
 * сбоя (пульт открыли, когда сервис уже лежал).
 */
export function isServiceDown(now: number): boolean {
  if (current.failingSince === null) return false;
  return now - (current.lastOkAt ?? current.failingSince) >= SERVICE_DOWN_AFTER_MS;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Состояние связи для экрана: перерисовка при каждой отметке. */
export function useConnection(): ConnectionState {
  return useSyncExternalStore(subscribe, getConnection, getConnection);
}

/** Начальное состояние — для тестов: связь общая на модуль. */
export function resetConnection(): void {
  set(INITIAL);
}
